import { FormValues } from './Type';
import { EstablishmentTypeData, SpecialEventData } from './EstablishmentType';

export interface SchemaField {
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  fieldValue: string | number | boolean;
  heading?: string;
  subHeading?: string;
  required?: boolean;
}

// Helper function to get establishment type display names
export const getEstablishmentTypeDisplayName = (type: string): string => {
  switch (type) {
    case 'Restaurant':
      return 'Restaurant';
    case 'RetailFoodEstablishment':
      return 'Retail Food Establishment';
    case 'MobileVehicle':
      return 'Mobile Vehicle';
    case 'Special Event':
      return 'Special Event';
    default:
      return type;
  }
};

export const FieldsSchema = (formData: FormValues): SchemaField[] => {
  const fields: SchemaField[] = [];

  // Establishment Information Section
  fields.push(
    {
      fieldName: 'establishmentInfo',
      fieldLabel: 'Establishment Information',
      fieldType: 'heading',
      fieldValue: '',
      heading: 'Establishment Information'
    },
    {
      fieldName: 'establishmentName',
      fieldLabel: 'Establishment Name',
      fieldType: 'text',
      fieldValue: formData.establishmentName || '',
      required: true
    },
    {
      fieldName: 'establishmentContactName',
      fieldLabel: 'Manager/Establishment Contact Name',
      fieldType: 'text',
      fieldValue: formData.establishmentContactName || '',
      required: true
    },
    {
      fieldName: 'blockLot',
      fieldLabel: 'Block/Lot',
      fieldType: 'text',
      fieldValue: formData.blockLot || '',
      required: true
    },
    {
      fieldName: 'establishmentAddress',
      fieldLabel: 'Street Address',
      fieldType: 'text',
      fieldValue: formData.establishmentAddress || '',
      required: true
    },
    {
      fieldName: 'establishmentTown',
      fieldLabel: 'Town',
      fieldType: 'text',
      fieldValue: formData.establishmentTown || '',
      required: true
    },
    {
      fieldName: 'establishmentEmail',
      fieldLabel: 'Email Address',
      fieldType: 'email',
      fieldValue: formData.establishmentEmail || '',
      required: true
    },
    {
      fieldName: 'establishmentPhone',
      fieldLabel: 'Phone Number',
      fieldType: 'text',
      fieldValue: formData.establishmentPhone || '',
      required: true
    }
  );

  // Owner Information Section
  fields.push(
    {
      fieldName: 'ownerInfo',
      fieldLabel: 'Owner Information',
      fieldType: 'heading',
      fieldValue: '',
      heading: 'Owner Information'
    },
    {
      fieldName: 'applicantName',
      fieldLabel: 'Name of Owner/Corporation',
      fieldType: 'text',
      fieldValue: formData.applicantName || '',
      required: true
    },
    {
      fieldName: 'applicantAddress',
      fieldLabel: 'Address',
      fieldType: 'text',
      fieldValue: formData.applicantAddress || '',
      required: true
    },
    {
      fieldName: 'applicantSuitePOBox',
      fieldLabel: 'Suite/P.O. Box',
      fieldType: 'text',
      fieldValue: formData.applicantSuitePOBox || ''
    },
    {
      fieldName: 'applicantCity',
      fieldLabel: 'City',
      fieldType: 'text',
      fieldValue: formData.applicantCity || '',
      required: true
    },
    {
      fieldName: 'applicantState',
      fieldLabel: 'State',
      fieldType: 'text',
      fieldValue: formData.applicantState || '',
      required: true
    },
    {
      fieldName: 'applicantZipCode',
      fieldLabel: 'Zip Code',
      fieldType: 'text',
      fieldValue: formData.applicantZipCode || '',
      required: true
    },
    {
      fieldName: 'applicantEmail',
      fieldLabel: 'Email',
      fieldType: 'email',
      fieldValue: formData.applicantEmail || '',
      required: true
    },
    {
      fieldName: 'applicantPhone',
      fieldLabel: 'Phone Number',
      fieldType: 'text',
      fieldValue: formData.applicantPhone || '',
      required: true
    }
  );

  // Establishment Type Section - ALWAYS SHOW ALL TYPES
  fields.push({
    fieldName: 'establishmentTypeInfo',
    fieldLabel: 'Establishment Type',
    fieldType: 'heading',
    fieldValue: '',
    heading: 'Establishment Type Selection'
  });

  // Define all possible establishment types - ALWAYS SHOW ALL
  const allEstablishmentTypes = ['Restaurant', 'RetailFoodEstablishment', 'MobileVehicle', 'Special Event'];
  
  allEstablishmentTypes.forEach(type => {
    const displayName = getEstablishmentTypeDisplayName(type);
    let isSelected = false;
    let typeData: any = null;

    // Check if this type exists in the form data and is selected
    if (formData.EstablishmentType && formData.EstablishmentType[type]) {
      typeData = formData.EstablishmentType[type];
      
      if (type === 'Special Event') {
        const specialEventData = typeData as SpecialEventData;
        isSelected = !!(specialEventData && specialEventData.status);
      } else {
        const establishmentTypeData = typeData as EstablishmentTypeData;
        isSelected = !!(establishmentTypeData && (
          establishmentTypeData.status === true || 
          establishmentTypeData[type] === true || 
          establishmentTypeData.selected === true
        ));
      }
    }

    // ALWAYS add the main field
    fields.push({
      fieldName: `${type}Status`,
      fieldLabel: displayName,
      fieldType: 'checkbox',
      fieldValue: isSelected,
      subHeading: `${displayName} Details`
    });

    // ALWAYS add sub-fields regardless of selection
    if (type === 'Special Event') {
      fields.push(
        {
          fieldName: 'eventParticipationType',
          fieldLabel: 'Event Participation Type',
          fieldType: 'text',
          fieldValue: (typeData as SpecialEventData)?.eventParticipationType || ''
        },
        {
          fieldName: 'eventAddress',
          fieldLabel: 'Event Address',
          fieldType: 'text',
          fieldValue: (typeData as SpecialEventData)?.eventAddress || ''
        },
        {
          fieldName: 'eventDateFrom',
          fieldLabel: 'Event Date From',
          fieldType: 'date',
          fieldValue: (typeData as SpecialEventData)?.eventDateFrom || ''
        },
        {
          fieldName: 'eventDateTo',
          fieldLabel: 'Event Date To',
          fieldType: 'date',
          fieldValue: (typeData as SpecialEventData)?.eventDateTo || ''
        },
        {
          fieldName: 'rainDateFrom',
          fieldLabel: 'Rain Date From',
          fieldType: 'date',
          fieldValue: (typeData as SpecialEventData)?.rainDateFrom || ''
        },
        {
          fieldName: 'rainDateTo',
          fieldLabel: 'Rain Date To',
          fieldType: 'date',
          fieldValue: (typeData as SpecialEventData)?.rainDateTo || ''
        }
      );
    } else {
      // Regular establishment types - ALWAYS add size and fee fields
      fields.push(
        {
          fieldName: `${type}Size`,
          fieldLabel: `${displayName} Size`,
          fieldType: 'text',
          fieldValue: (typeData as EstablishmentTypeData)?.size || ''
        },
        {
          fieldName: `${type}Fee`,
          fieldLabel: `${displayName} Fee`,
          fieldType: 'currency',
          fieldValue: (typeData as EstablishmentTypeData)?.fee || ''
        }
      );
    }
  });

  // Additional Information Section
  fields.push({
    fieldName: 'additionalInfo',
    fieldLabel: 'Additional Information',
    fieldType: 'heading',
    fieldValue: '',
    heading: 'Additional Information'
  });

  // ALWAYS show secondary contact status
  fields.push({
    fieldName: 'hasSecondaryContactInfo',
    fieldLabel: 'Has Secondary Contact Information',
    fieldType: 'checkbox',
    fieldValue: formData.hasSecondaryContactInfo ? 'Yes' : 'No'
  });

  // ALWAYS show secondary contact fields
  fields.push({
    fieldName: 'secondaryContactDetails',
    fieldLabel: 'Secondary Contact Details',
    fieldType: 'subheading',
    fieldValue: '',
    subHeading: 'Secondary Contact Information'
  });
  
  fields.push(
    {
      fieldName: 'secondaryContactName',
      fieldLabel: 'Secondary Contact Name',
      fieldType: 'text',
      fieldValue: formData.secondaryContactName || ''
    },
    {
      fieldName: 'secondaryDepartment',
      fieldLabel: 'Secondary Department',
      fieldType: 'text',
      fieldValue: formData.secondaryDepartment || ''
    },
    {
      fieldName: 'secondaryAddress',
      fieldLabel: 'Secondary Address',
      fieldType: 'text',
      fieldValue: formData.secondaryAddress || ''
    },
    {
      fieldName: 'secondaryEmail',
      fieldLabel: 'Secondary Email',
      fieldType: 'email',
      fieldValue: formData.secondaryEmail || ''
    },
    {
      fieldName: 'secondaryPhone',
      fieldLabel: 'Secondary Phone Number',
      fieldType: 'text',
      fieldValue: formData.secondaryPhone || ''
    }
  );

  // Document Upload Section
  if (formData.DocumentFileUpload && formData.DocumentFileUpload.length > 0) {
    fields.push({
      fieldName: 'documentUpload',
      fieldLabel: 'Document Upload',
      fieldType: 'heading',
      fieldValue: '',
      heading: 'Uploaded Documents'
    });

    formData.DocumentFileUpload.forEach((doc, index) => {
      fields.push({
        fieldName: `document_${index}`,
        fieldLabel: `Document ${index + 1}`,
        fieldType: 'text',
        fieldValue: `${doc.Type}: ${doc.FileName}`
      });
    });
  }

  // Application Summary Section
  fields.push({
    fieldName: 'applicationSummary',
    fieldLabel: 'Application Summary',
    fieldType: 'heading',
    fieldValue: '',
    heading: 'Application Summary'
  });

  // Calculate total fees from establishment types
  let totalEstablishmentFees = 0;
  if (formData.EstablishmentType) {
    Object.values(formData.EstablishmentType).forEach((data) => {
      if (data && typeof data === 'object' && 'fee' in data && typeof data.fee === 'number') {
        totalEstablishmentFees += data.fee;
      }
    });
  }

  // Add fee summary
  fields.push({
    fieldName: 'totalEstablishmentFees',
    fieldLabel: 'Total Establishment Type Fees',
    fieldType: 'currency',
    fieldValue: totalEstablishmentFees || ''
  });

  // Application metadata
  fields.push(
    {
      fieldName: 'applicationType',
      fieldLabel: 'Application Type',
      fieldType: 'text',
      fieldValue: 'New Application'
    },
    {
      fieldName: 'applicationDate',
      fieldLabel: 'Application Date',
      fieldType: 'date',
      fieldValue: new Date().toLocaleDateString()
    }
  );

  return fields;
};

// Helper function to format field values for PDF display
export const formatFieldValue = (field: SchemaField): string => {
  switch (field.fieldType) {
    case 'currency':
      if (field.fieldValue && typeof field.fieldValue === 'number') {
        return `${field.fieldValue.toFixed(2)}`;
      }
      return field.fieldValue.toString();
    case 'date':
      if (field.fieldValue && field.fieldValue !== '') {
        const date = new Date(field.fieldValue.toString());
        return date.toLocaleDateString();
      }
      return '';
    case 'checkbox':
      return field.fieldValue ? 'Yes' : 'No';
    case 'email':
    case 'text':
    default:
      return field.fieldValue.toString();
  }
};

// Helper function to group fields by section for better PDF organization
export const groupFieldsBySection = (fields: SchemaField[]): Record<string, SchemaField[]> => {
  const sections: Record<string, SchemaField[]> = {};
  let currentSection = 'General';

  fields.forEach((field) => {
    if (field.fieldType === 'heading') {
      currentSection = field.fieldLabel;
      sections[currentSection] = [];
    } else if (field.fieldType !== 'subheading') {
      if (!sections[currentSection]) {
        sections[currentSection] = [];
      }
      sections[currentSection].push(field);
    }
  });

  return sections;
};

export default FieldsSchema;